import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { shipmentService } from '../services/firebase';
import { Shipment } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import { MapPinIcon, UserCircleIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';

const ShipmentDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isStatusSaving, setIsStatusSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<any>({});
  const { role } = useAuth();

  useEffect(() => {
    const fetchShipment = async () => {
      if (!id) {
        setError('No shipment ID provided.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const fetchedShipment = await shipmentService.getShipment(id);
        if (fetchedShipment) {
          setShipment(fetchedShipment);
          setEditData(fetchedShipment);
        } else {
          setError('Shipment not found.');
        }
      } catch (err) {
        setError('Failed to fetch shipment.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchShipment();
  }, [id]);
  
  const handleStatusChange = async (newStatus: string) => {
    if (!shipment) return;
    
    const newStatusTyped = newStatus as Shipment['status'];

    setIsStatusSaving(true);
    try {
      await shipmentService.updateShipment(shipment.id, { status: newStatusTyped });
      setShipment({ ...shipment, status: newStatusTyped });
      setEditData((prev: any) => ({ ...prev, status: newStatusTyped }));
    } catch (err) {
      console.error("Failed to update status:", err);
    } finally {
      setIsStatusSaving(false);
    }
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shipment) return;
    try {
      await shipmentService.updateShipment(shipment.id, editData);
      setShipment({ ...shipment, ...editData });
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to update shipment:', err);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-center text-red-500 py-12">{error}</div>;
  if (!shipment) return <div className="text-center text-gray-500 py-12">Shipment data not available.</div>;

  const { trackingNumber, status, customer, origin, destination, estimatedDeliveryDate, history } = shipment;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Shipment</h1>
          <p className="text-lg text-gray-600 font-mono">{trackingNumber}</p>
        </div>
        {role === 'admin' || role === 'superadmin' ? (
          <div className="text-right space-x-2">
            <label htmlFor="status" className="sr-only">Status</label>
            <select
              id="status"
              value={editData.status || status}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={isStatusSaving}
              className={`capitalize appearance-none w-full bg-transparent font-semibold py-1 pl-3 pr-8 border rounded-full transition ${
                isStatusSaving ? 'cursor-not-allowed bg-gray-200' : 'hover:border-gray-400'
              } ${
                status === 'delivered' ? 'border-green-300 bg-green-100 text-green-800' :
                status === 'in_transit' ? 'border-blue-300 bg-blue-100 text-blue-800' :
                status === 'cancelled' ? 'border-red-300 bg-red-100 text-red-800' : 'border-yellow-300 bg-yellow-100 text-yellow-800'
              }`}
            >
              <option value="pending">Pending</option>
              <option value="in_transit">In Transit</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <button
              className="ml-2 px-4 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
              onClick={() => setIsEditing((prev) => !prev)}
            >
              {isEditing ? 'Cancel' : 'Edit'}
            </button>
          </div>
        ) : null}
      </div>

      {/* Main Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
            {/* Origin & Destination */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                 <div className="flex items-center justify-between">
                    <div className="text-center">
                        <h3 className="font-bold text-lg">{origin?.city || 'N/A'}</h3>
                        <p className="text-sm text-gray-500">{origin?.state || 'N/A'}, {origin?.country || 'N/A'}</p>
                    </div>
                    <div className="text-center flex-1">
                        <div className="border-t-2 border-dashed mx-4"></div>
                    </div>
                    <div className="text-center">
                        <h3 className="font-bold text-lg">{destination?.city || 'N/A'}</h3>
                        <p className="text-sm text-gray-500">{destination?.state || 'N/A'}, {destination?.country || 'N/A'}</p>
                    </div>
                </div>
            </div>

             {/* Map Placeholder */}
            <div className="bg-white p-6 rounded-lg shadow-md h-64 flex items-center justify-center">
                <div className="text-center text-gray-400">
                    <MapPinIcon className="h-16 w-16 mx-auto" />
                    <p className="mt-2">Live map tracking coming soon</p>
                </div>
            </div>
            
            {/* Shipment History */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-xl font-bold mb-4">History</h2>
                <ul className="space-y-4">
                    {history && history.length > 0 ? (
                        history.map((entry, index) => (
                            <li key={index} className="flex items-start">
                                <div className="flex-shrink-0">
                                    <div className={`h-8 w-8 rounded-full flex items-center justify-center ${index === 0 ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>
                                        <CalendarIcon className="h-5 w-5" />
                                    </div>
                                </div>
                                <div className="ml-4">
                                    <p className="font-semibold">{entry.status.replace('_', ' ')} - {entry.location}</p>
                                    <p className="text-sm text-gray-500">{new Date(entry.date).toLocaleString()}</p>
                                </div>
                            </li>
                        ))
                    ) : (
                        <li className="text-center text-gray-500 py-4">
                            No shipment history available.
                        </li>
                    )}
                </ul>
            </div>
        </div>

        {/* Sidebar Info */}
        <div className="space-y-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-xl font-bold mb-4 flex items-center">
                    <UserCircleIcon className="h-6 w-6 mr-2 text-gray-500"/>
                    Customer
                </h2>
                 {customer ? (
                    <Link to={`/customers/${customer.id}`} className="text-blue-600 hover:underline">
                        <p className="font-bold">{customer.name}</p>
                        <p className="text-sm">{customer.email}</p>
                    </Link>
                ) : <p className="italic text-gray-500">No customer assigned.</p>}
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-xl font-bold mb-4">Details</h2>
                <dl className="space-y-2 text-sm">
                    <div className="flex justify-between">
                        <dt className="text-gray-500">Est. Delivery:</dt>
                        <dd className="font-semibold">{new Date(estimatedDeliveryDate).toLocaleDateString()}</dd>
                    </div>
                </dl>
            </div>
        </div>
      </div>

      {/* Editable Form for Admins */}
      {isEditing && (role === 'admin' || role === 'superadmin') ? (
        <form onSubmit={handleEditSubmit} className="bg-white p-6 rounded-lg shadow-md mb-8">
          <h2 className="text-xl font-bold mb-4">Edit Shipment</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea name="description" value={editData.description || ''} onChange={handleEditChange} className="input w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Weight (kg)</label>
              <input type="number" name="weight" value={editData.weight || ''} onChange={handleEditChange} className="input w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Origin Address</label>
              <input type="text" name="origin.address" value={editData.origin?.address || ''} onChange={handleEditChange} className="input w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Destination Address</label>
              <input type="text" name="destination.address" value={editData.destination?.address || ''} onChange={handleEditChange} className="input w-full" />
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <button type="submit" className="btn-primary">Save Changes</button>
          </div>
        </form>
      ) : null}
    </div>
  );
};

export default ShipmentDetail; 